import {
  Badge,
  Button,
  Dialog,
  DialogActions,
  DialogBody,
  DialogContent,
  DialogSurface,
  DialogTitle,
  Dropdown,
  MessageBar,
  MessageBarBody,
  Option,
  Spinner,
  Text,
  makeStyles,
  tokens,
} from '@fluentui/react-components';
import {
  payrollPeriodFor,
  shiftPayrollPeriod,
  todayInZone,
  type PayrollPeriod,
} from '@worklogix/shared';
import { useCallback, useEffect, useMemo, useState } from 'react';

import { toErrorMessage } from '../errors';

/**
 * The "Post to NetSuite" control shared by Bonus, Commission and Reimbursements.
 *
 * NOTHING POSTS AUTOMATICALLY (Michael, 2026-09-13: "everything should be
 * manually approved or submitted"). This component IS the manual submission: a
 * human opens it, reads the per-person plan, and clicks again. Two deliberate
 * clicks, and the second one names the amount and the headcount so it cannot be
 * mistaken for a navigation button.
 *
 * WHY A PREVIEW IS MANDATORY AND NOT A CONVENIENCE. The plan comes from the same
 * server-side builder the write uses, so what is approved on screen is derived
 * from the same inputs that move the money — not a description of it written
 * separately and free to drift. It also answers the question a supervisor
 * actually has, which is never "will this work?" but "who is about to be paid,
 * who is being skipped, and why".
 *
 * BLOCKED ROWS ARE SHOWN, NOT HIDDEN. A person who will not be paid is the most
 * important row on the screen; hiding them turns a visible problem into a
 * payroll complaint next week. The confirm button stays enabled when only SOME
 * rows are blocked — posting the ones that can go through is right, and the rest
 * are named so they can be fixed and re-posted. It is disabled only when there
 * is nothing at all to post.
 *
 * THE RESULT DIALOG DOES NOT AUTO-CLOSE. A partial post is the case that matters
 * and it must be read, not dismissed by the same click that caused it.
 */

const useStyles = makeStyles({
  field: {
    display: 'flex',
    flexDirection: 'column',
    gap: tokens.spacingVerticalXXS,
    marginBottom: tokens.spacingVerticalM,
    minWidth: 0,
  },
  list: { display: 'flex', flexDirection: 'column', gap: tokens.spacingVerticalXS },
  row: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: tokens.spacingHorizontalM,
    flexWrap: 'wrap',
    paddingTop: tokens.spacingVerticalXS,
    paddingBottom: tokens.spacingVerticalXS,
    borderBottom: `1px solid ${tokens.colorNeutralStroke3}`,
  },
  name: { minWidth: 0, overflowWrap: 'anywhere' },
  right: {
    display: 'flex',
    alignItems: 'center',
    gap: tokens.spacingHorizontalS,
    fontVariantNumeric: 'tabular-nums',
  },
  // The plan can run to every person on the report; the dialog must scroll rather
  // than push its own confirm button off the bottom of a phone screen.
  scroll: { maxHeight: '46vh', overflowY: 'auto' },
  total: { marginTop: tokens.spacingVerticalM },
  muted: { color: tokens.colorNeutralForeground3 },
  bad: { color: tokens.colorPaletteRedForeground1 },
});

/** One person on the plan, flattened so all three reports render identically. */
export interface PostPlanRowView {
  key: string;
  name: string;
  amount: number;
  /** `post` = will be written; `skip` = already posted; `blocked` = cannot. */
  action: 'post' | 'skip' | 'blocked';
  /**
   * Explicitly `| undefined`: under `exactOptionalPropertyTypes` the server's
   * optional `reason` cannot be spread into a bare `reason?: string`, and the
   * adapters map the field straight across rather than inventing a placeholder.
   */
  reason?: string | undefined;
}

/** A whole period's plan, as this dialog needs it. */
export interface PostPlanView {
  /** The NetSuite payroll batch the cheques live in; null when none is open. */
  batchNumber: string | null;
  rows: readonly PostPlanRowView[];
  totalToPost: number;
}

/** What posting actually did. Per person, because a partial run must be legible. */
export interface PostResultView {
  posted: ReadonlyArray<{ name: string; amount: number }>;
  failed: ReadonlyArray<{ name: string; reason: string }>;
}

interface PostToNetSuiteProps {
  /** Lower-case noun for the sentences: "bonus", "commission", "reimbursement". */
  what: string;
  /**
   * The period to post, FIXED — the dialog shows no picker at all. Commission uses
   * this: its own period control sits directly above the button.
   */
  payPeriodEnding?: string;
  /**
   * The period the dialog's own picker OPENS ON.
   *
   * 🔥 MICHAEL, 2026-09-30: "the pay period populated in the modal needs to always be
   * the pay period that is selected in the actual report page. It keeps defaulting to
   * the current payroll period and it needs to stop because Abbye is having to correct
   * the payroll period twice."
   *
   * The dialog used to open on the CURRENT week no matter which week the table behind
   * it was showing. Reviewing last week's bonuses and pressing Post therefore offered
   * a plan for a different week — correct-looking, and about the wrong money. Every
   * post was two corrections: one on the page, one in here.
   *
   * WHY THE PICKER STAYS rather than the period simply being fixed to the page's: the
   * page offers "All periods", which is not a thing that can be posted, and a post has
   * to name exactly one week. Seeding it answers the common case without removing the
   * only control that can resolve the uncommon one.
   */
  defaultPayPeriodEnding?: string | undefined;
  disabled?: boolean;
  loadPlan: (payPeriodEnding: string) => Promise<PostPlanView>;
  post: (payPeriodEnding: string) => Promise<PostResultView>;
  /** Refresh the caller's table — posted rows change state. */
  onPosted?: () => void | Promise<void>;
}

const money = (n: number): string =>
  n.toLocaleString(undefined, { style: 'currency', currency: 'USD' });

export function PostToNetSuite({
  what,
  payPeriodEnding,
  defaultPayPeriodEnding,
  disabled = false,
  loadPlan,
  post,
  onPosted,
}: PostToNetSuiteProps): JSX.Element {
  const styles = useStyles();

  const [open, setOpen] = useState(false);
  const [plan, setPlan] = useState<PostPlanView | null>(null);
  const [loading, setLoading] = useState(false);
  const [posting, setPosting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [result, setResult] = useState<PostResultView | null>(null);

  const currentPeriod = useMemo<PayrollPeriod>(
    () => payrollPeriodFor(todayInZone(new Date())),
    []
  );
  const ownsPeriod = payPeriodEnding === undefined;

  /** This period and the previous eight — the depth every other period picker offers. */
  const choices = useMemo(
    () => Array.from({ length: 9 }, (_, i) => shiftPayrollPeriod(currentPeriod, -i)),
    [currentPeriod]
  );

  /**
   * Which of those the picker starts on: the page's period when it names one we can
   * offer, else the current week.
   *
   * Resolved by END DATE rather than by an offset the caller computes, so the two
   * surfaces cannot disagree about which week a number means — and a page showing
   * something outside this depth (or "All periods") falls back visibly to the current
   * week instead of silently landing on an unrelated one.
   */
  const initialOffset = useMemo(() => {
    if (defaultPayPeriodEnding === undefined) {
      return 0;
    }
    const found = choices.findIndex((p) => p.endDate === defaultPayPeriodEnding);
    return found === -1 ? 0 : -found;
  }, [choices, defaultPayPeriodEnding]);

  const [offset, setOffset] = useState(initialOffset);
  /*
   * Re-seed whenever the page's period changes, INCLUDING while the dialog is shut.
   * Abbye's sequence is: change the week on the page, then press Post. If the seed only
   * applied on first mount, the second post of a session would open on the first post's
   * week -- the same bug in a less obvious place.
   */
  useEffect(() => {
    setOffset(initialOffset);
  }, [initialOffset]);

  const shown = useMemo(
    () => shiftPayrollPeriod(currentPeriod, offset),
    [currentPeriod, offset]
  );
  const period = ownsPeriod ? shown.endDate : payPeriodEnding;

  useEffect(() => {
    if (!open) {
      return;
    }
    let active = true;
    setLoading(true);
    setError(null);
    setPlan(null);
    loadPlan(period)
      .then((p) => {
        if (active) {
          setPlan(p);
        }
      })
      .catch((err: unknown) => {
        if (active) {
          // Never fall through to an empty plan: "nothing to post" and "we could
          // not find out" look identical on screen and mean opposite things.
          setError(toErrorMessage(err));
        }
      })
      .finally(() => {
        if (active) {
          setLoading(false);
        }
      });
    return () => {
      active = false;
    };
  }, [loadPlan, open, period]);

  const confirm = useCallback((): void => {
    setPosting(true);
    setError(null);
    void post(period)
      .then(async (r) => {
        setResult(r);
        await onPosted?.();
      })
      .catch((err: unknown) => {
        setError(toErrorMessage(err));
      })
      .finally(() => {
        setPosting(false);
      });
  }, [onPosted, period, post]);

  const close = useCallback((): void => {
    setOpen(false);
    setPlan(null);
    setResult(null);
    setError(null);
  }, []);

  const toPost = plan?.rows.filter((r) => r.action === 'post') ?? [];
  const confirmLabel = `Post ${money(plan?.totalToPost ?? 0)} to ${String(toPost.length)} ${
    toPost.length === 1 ? 'paycheck' : 'paychecks'
  }`;

  return (
    <>
      <Button
        appearance="primary"
        disabled={disabled}
        onClick={() => {
          setOpen(true);
        }}
      >
        Post to NetSuite
      </Button>

      <Dialog
        open={open}
        onOpenChange={(_, data) => {
          if (!data.open) {
            close();
          }
        }}
      >
        <DialogSurface>
          <DialogBody>
            <DialogTitle>
              {result === null ? `Post ${what} to NetSuite` : `Posted ${what}`}
            </DialogTitle>

            <DialogContent>
              {result !== null ? (
                <div className={styles.list}>
                  {result.posted.length > 0 && (
                    <>
                      <Text weight="semibold">{`${String(result.posted.length)} posted`}</Text>
                      {result.posted.map((p) => (
                        <div key={p.name} className={styles.row}>
                          <Text className={styles.name}>{p.name}</Text>
                          <span className={styles.right}>{money(p.amount)}</span>
                        </div>
                      ))}
                    </>
                  )}
                  {result.failed.length > 0 && (
                    <>
                      <Text weight="semibold" className={styles.bad}>
                        {`${String(result.failed.length)} did not post`}
                      </Text>
                      {result.failed.map((f) => (
                        <div key={f.name} className={styles.row}>
                          <Text className={styles.name}>{f.name}</Text>
                          <Text size={200} className={styles.bad}>
                            {f.reason}
                          </Text>
                        </div>
                      ))}
                    </>
                  )}
                  {result.posted.length === 0 && result.failed.length === 0 && (
                    <Text>Nothing was posted.</Text>
                  )}
                </div>
              ) : (
                <>
                  {ownsPeriod && (
                    <div className={styles.field}>
                      <Text size={200}>Pay period</Text>
                      <Dropdown
                        disabled={posting}
                        value={`${shown.startDate} – ${shown.endDate}${
                          offset === 0 ? ' (current)' : ''
                        }`}
                        selectedOptions={[String(offset)]}
                        onOptionSelect={(_, d) => {
                          setOffset(Number(d.optionValue ?? 0));
                        }}
                      >
                        {choices.map((p, i) => {
                          const text = `${p.startDate} – ${p.endDate}${i === 0 ? ' (current)' : ''}`;
                          return (
                            <Option key={p.endDate} value={String(-i)} text={text}>
                              {text}
                            </Option>
                          );
                        })}
                      </Dropdown>
                    </div>
                  )}

                  {error !== null && (
                    <MessageBar intent="error">
                      <MessageBarBody>{error}</MessageBarBody>
                    </MessageBar>
                  )}

                  {loading && <Spinner label="Checking what would post…" />}

                  {plan !== null && !loading && (
                    <>
                      {plan.batchNumber === null ? (
                        <MessageBar intent="warning">
                          <MessageBarBody>
                            There is no open payroll batch for this period, so nothing can
                            post yet. Create the batch in NetSuite first.
                          </MessageBarBody>
                        </MessageBar>
                      ) : (
                        <Text size={200} className={styles.muted}>
                          {`Payroll batch ${plan.batchNumber}. Each amount is added to that person's paycheck as an earning line.`}
                        </Text>
                      )}

                      <div className={styles.scroll}>
                        <div className={styles.list}>
                          {plan.rows.map((r) => (
                            <div key={r.key} className={styles.row}>
                              <Text className={styles.name}>{r.name}</Text>
                              <span className={styles.right}>
                                {money(r.amount)}
                                {r.action === 'post' && (
                                  <Badge appearance="filled" color="success" size="small">
                                    Will post
                                  </Badge>
                                )}
                                {r.action === 'skip' && (
                                  <Badge appearance="tint" color="informative" size="small">
                                    {r.reason ?? 'Skipped'}
                                  </Badge>
                                )}
                                {r.action === 'blocked' && (
                                  <Badge appearance="filled" color="danger" size="small">
                                    {r.reason ?? 'Blocked'}
                                  </Badge>
                                )}
                              </span>
                            </div>
                          ))}
                          {plan.rows.length === 0 && (
                            <Text>{`No ${what} entries in this pay period.`}</Text>
                          )}
                        </div>
                      </div>

                      <Text weight="semibold" className={styles.total}>
                        {confirmLabel.replace('Post ', '')}
                      </Text>
                    </>
                  )}
                </>
              )}
            </DialogContent>

            <DialogActions>
              <Button onClick={close} disabled={posting}>
                {result === null ? 'Cancel' : 'Close'}
              </Button>
              {result === null && (
                <Button
                  appearance="primary"
                  // Disabled only when there is genuinely nothing to write. Blocked
                  // rows do not stop the rest — they are named above and can be
                  // fixed and re-posted.
                  disabled={posting || loading || toPost.length === 0}
                  onClick={confirm}
                >
                  {posting ? 'Posting…' : confirmLabel}
                </Button>
              )}
            </DialogActions>
          </DialogBody>
        </DialogSurface>
      </Dialog>
    </>
  );
}
